import { createContext, useContext, useEffect, useReducer } from 'react'
import { useNavigate } from 'react-router'
import { formDataToObject, Logger, parseFields } from '@wolfdanepug/common-react'
import { useApolloClient } from '@apollo/client'
import useGetStartedSteps, { segmentMap } from '../../lib/useOnboardingSteps'
import { OnboardingState, StepSegment } from '../../types'
import { AppContext } from '../app/AppContext'
import { DataContext } from '../data/DataContext'
import reducer from './reducer'
import initialState from './state'
import {
	PUSH_COMPLETED_SECTION,
	PUSH_COMPLETED_SEGMENT,
	RESET,
	SET_ACTIONS_OPEN,
	SET_ACTIONS_TEXT,
	SET_ACTIVE,
	SET_CAN_SKIP_STEP,
	SET_CAN_STEP_FORWARD,
	SET_DISCOUNT,
	SET_INITIAL_STEP_DATA,
	SET_NAVIGATION_OPEN,
	SET_SELECTED_DOG
} from './actions'
// import { checkout as storeCheckout } from '@/api'

const getNextStepUri = (nextStep: any, data: any) => {
	return typeof nextStep.uri === 'function' ? nextStep.uri(data) : nextStep.uri
}

const beforeSaveDataHandler = (segment: any, raw: any, data: any) => {
	return typeof segment.beforeSaveData === 'function' ? segment.beforeSaveData({ raw, data }) : data
}

// const saveDataHandler = async (segment: any, data: any) => {
// 	return await segment.saveData(data)
// }

const afterSaveDataHandler = (segment: any, data: any) => {
	typeof segment.afterSaveData === 'function' ? segment.afterSaveData(data) : Promise.resolve()
}

export const OnboardingContext = createContext({
	useOnboardingState: () => ({}),
	pushData: (section: number, segment: number, data: any) => {},
	reset: () => {},
	setInitialStepData: (data: any) => {},
	setCanStepForward: (can: boolean) => {},
	setCanSkipStep: (can: boolean) => {},
	getSectionBySegment: (segment: string) => ({}),
	completeSegment: (segment: string) => {},
	setActionsOpen: (open: boolean) => {},
	setActionsText: (text: string) => {},
	useFormData: () => ({}),
	completeSection: (section: string) => {},
	goBackward: () => {},
	setDiscount: (discount: string) => {},
	setActive: (params: { sectionIndex: number; segmentIndex: number }) => {},
	checkout: (input: any) => Promise.resolve({}),
	setNavigationOpen: (open: boolean) => {},
	setSelectedDog: (index: number) => {},
	skipStep: () => {},
	useStepCompleted: () => false,
	onSubmit: (e: any) => {}
	// getPlanQuote: (input: any) => {},
	// usePlanQuote: () => ({})
})

export interface OnboardingProviderProps {
	children: any
	defaultState?: any
}

export const OnboardingProvider = ({ children, defaultState }: OnboardingProviderProps) => {
	const [state, dispatch] = useReducer(reducer, defaultState || initialState)
	const { setLoading } = useContext(AppContext)
	const { saveData } = useContext(DataContext)
	const steps = useGetStartedSteps()
	const navigate = useNavigate()

	const apollo = useApolloClient()

	// useBeforeUnload(Boolean(state?.rawData))

	/**
	 * Dispatch action controller
	 * @param {string} type
	 * @param {any} payload
	 */
	const controller = (type: string, payload: any = {}): void => {
		dispatch({ type, payload })
	}

	useEffect(() => {
		Logger.log('[OnboardingProvider] state', state)
	}, [state])

	/**
	 * Set Active Section and Segment
	 * @param { sectionIndex: number; segmentIndex: number } params
	 * @example setActive({ sectionIndex: 0, segmentIndex: 0 })
	 * @returns {void}
	 */
	const setActive = ({
		sectionIndex,
		segmentIndex
	}: {
		sectionIndex: number
		segmentIndex: number
	}): void => {
		controller(SET_ACTIVE, { activeSection: sectionIndex, activeSegment: segmentIndex })
	}

	/**
	 * Move to the next step
	 * @param { any } step
	 * @param { any } nextStep
	 * @param { any } data
	 * @example toNextStep(step, nextStep, data)
	 * @returns {void}
	 */
	const toNextStep = (step: any, nextStep: any, data: any): void => {
		// get the current section
		const currentSection = steps[state.activeSection]
		// get the total number of segments in the current section
		const stepSectionTotalSegments = currentSection?.segments?.length || 0
		Logger.log('[OnboardingProvider] toNextStep', { currentSection, step, nextStep, data })
		// if the current segment is the last segment in the section, mark the section as
		// completed and mark the current segment as completed
		if (stepSectionTotalSegments - 1 === state.activeSegment || step.terminate) {
			controller(PUSH_COMPLETED_SECTION, currentSection.name)
		}
		// // set the active section and segment
		// setActive(nextStep)
		// get the uri for the next step
		const uri = getNextStepUri(nextStep, data)
		// navigate to the next step
		navigate(uri)
		controller(PUSH_COMPLETED_SEGMENT, step.name)
		// controller(SET_CAN_STEP_FORWARD, false)
	}

	const nextStepHandler = (segment: any, data: any) => {
		Logger.log('[OnboardingProvider] nextStepHandler', segment, data)
		return segmentMap[
			typeof segment.nextStep === 'function' ? segment.nextStep(data) : segment.nextStep
		]
	}

	/**
	 * Push data to the state
	 * @param { number } section
	 * @param { number } segment
	 * @param { any } data
	 * @example pushData(0, 0, { raw: { name: 'John Doe', dogs[0][name]: 'Rocco' }, data: { name: 'John Doe', dogs: [{ name: Rocco }] } })
	 * @returns {void}
	 */
	const pushData = async (section: number, segment: number, { raw, data }: any): Promise<void> => {
		// get the segment data
		const segmentData: any = steps[section]?.segments?.[segment]
		Logger.log('[OnboardingProvider] pushData', { section, segment, raw, data, segmentData })
		// if the segment has a beforeSaveData function, call it
		// and pass the merged raw data and the data
		const segmentBeforeSaveData = beforeSaveDataHandler(segmentData, raw, data)
		// save the data
		const segmentSavedData = await saveData(segmentBeforeSaveData)
		Logger.log('Saved Data', segmentSavedData)
		// if the segment has an afterSafeData function, call it
		afterSaveDataHandler(segmentData, segmentSavedData?.data || {})
		// if the segment has a nextStep function, call it
		const nextStep = nextStepHandler(segmentData, segmentSavedData?.data || {})
		// go to next step
		toNextStep(segmentData, nextStep, segmentSavedData)
	}

	const skipStep = (): void => {
		const segment = Object.values(segmentMap).find(
			(segment: any) =>
				segment.sectionIndex === state.activeSection && segment.segmentIndex === state.activeSegment
		)
		if (!segment) return
		const data = parseFields(state.rawData)
		const nextStep = segmentMap[segment.nextStep]
		const uri = typeof nextStep.uri === 'function' ? nextStep.uri(data) : nextStep.uri
		Logger.log('[OnboardingProvider] skipStep - %s', uri, nextStep, data, state)
		navigate(uri as string)
	}

	/**
	 * Get the section by segment
	 * @param { string } segment
	 * @example getSectionBySegment('dog-info')
	 * @returns { StepSegment }
	 */
	const getSectionBySegment = (segment: string): StepSegment => {
		const sectionIndex = segmentMap[segment as any].sectionIndex
		return steps[sectionIndex as number]
	}

	/**
	 * @deprecated
	 * Use form data
	 * @example useFormData()
	 * @returns { any }
	 */
	const useFormData = (): any => {
		return state?.rawData ? parseFields(state.rawData) : {}
	}

	/**
	 * Use getting started state
	 * @example useOnboardingState()
	 * @returns { OnboardingState }
	 */
	const useOnboardingState = (): OnboardingState => {
		return state
	}

	const useStepCompleted = () => {
		const segment = steps[state.activeSection]?.segments?.[state.activeSegment]
		if (!segment) return false
		return state.completedSegments.includes(segment.name)
	}

	const checkout = async (input: any): Promise<any> => {
		Logger.log('[OnboardingProvider] checkout', input)
	}

	const goBackward = () => {
		navigate(-1)
	}

	const onSubmit = async (e: any) => {
		e.preventDefault()
		const fd = new FormData(e.target)
		const raw = formDataToObject(fd)
		const data = parseFields(raw)
		await pushData(state.activeSection, state.activeSegment, { raw, data })
	}

	return (
		<OnboardingContext.Provider
			value={{
				useOnboardingState,
				useFormData,
				pushData,
				getSectionBySegment,
				goBackward,
				reset: () => controller(RESET),
				setInitialStepData: (data: any) => controller(SET_INITIAL_STEP_DATA, data),
				setCanStepForward: (can: boolean) => controller(SET_CAN_STEP_FORWARD, can),
				setCanSkipStep: (can: boolean) => controller(SET_CAN_SKIP_STEP, can),
				completeSegment: (segment: string) => controller(PUSH_COMPLETED_SEGMENT, segment),
				setActionsOpen: (open: boolean) => controller(SET_ACTIONS_OPEN, open),
				setActionsText: (text: string) => controller(SET_ACTIONS_TEXT, text),
				completeSection: (section: string) => controller(PUSH_COMPLETED_SECTION, section),
				setDiscount: (discount: string) => controller(SET_DISCOUNT, discount),
				setActive: (params: { sectionIndex: number; segmentIndex: number }) => setActive(params),
				checkout,
				setNavigationOpen: (open: boolean) => controller(SET_NAVIGATION_OPEN, open),
				setSelectedDog: (index: number) => controller(SET_SELECTED_DOG, index),
				skipStep,
				useStepCompleted,
				onSubmit
			}}
		>
			{children}
		</OnboardingContext.Provider>
	)
}
